import React from 'react';
import styled from 'styled-components';

import { Gutter } from '../../layout/Gutter/Gutter';
import type { GutterProps } from '../../layout/Gutter/Gutter';

export type DividerProps = Omit<
  GutterProps,
  'as' | 'children' | 'className' | 'name'
> & {
  /** Put between the children. Ignored by the rule. */
  character?: string;
  /**
   * **Whether there are any decides what this is.** With children, they are joined inline by
   * `character` - `Boston • MA • Remote`. Without, it is a rule across the flow, and the spacing
   * props are the gutter around it - ignored inline.
   */
  children?: React.ReactNode;
  name?: string;
};

const StyledDivider = styled.span`
  margin: 0 3px;
  display: inline-block;
`;

/**
 * Divider - I mark a break between content: inline between children, or as a rule between runs of
 * content that no box encloses. ONLY concerned with the mark and the space it owns.
 */
export const Divider = ({
  bottom,
  character = '\u{2022}',
  children,
  gutterType,
  horizontal,
  left,
  name,
  only,
  right,
  top,
  vertical,
}: DividerProps) => {
  /*
    `undefined` only, not "renders nothing": a caller joining items that are all conditionally
    absent passes an empty list and expects nothing, not a line.
  */
  if (children === undefined) {
    return (
      <Gutter
        bottom={bottom}
        gutterType={gutterType}
        horizontal={horizontal}
        left={left}
        only={only}
        right={right}
        top={top}
        vertical={vertical}
      >
        <div className="divider-rule" data-test={name} />
      </Gutter>
    );
  }

  const items = React.Children.toArray(children).filter(
    child => child || child === 0
  );

  return (
    <div data-test={name}>
      {items.map((child, index) => (
        <span key={`group-${index.toString()}`}>
          {child}
          {index < items.length - 1 && (
            <StyledDivider>{character}</StyledDivider>
          )}
        </span>
      ))}
    </div>
  );
};
